import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from "axios";
import L from "leaflet";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { normalizeSearchText } from '@/utils/stringUtils';
import DeleteNameConfirm, { deleteNameMatches } from '@/components/DeleteNameConfirm';
import { MapContainer, TileLayer, Marker, Polygon, Polyline, Popup, Tooltip, useMapEvents, CircleMarker } from 'react-leaflet';
import { MapLayerTile, MapLayerToggle, useMapLayer } from '@/components/map/MapLayerToggle';
import { MapSearchBox, RecenterMap } from '@/components/map/MapSearchBox';
import {
  NEARBY_SOURCES,
  FitBoundsOnChange,
  NearbyLayerPanel,
  NearbyPointMarkers,
  NearestPlaces,
  buildDirectionsUrl,
  formatDistance,
  openInNewTab,
  pinIcon,
  toCoord,
  useNearbyPoints,
  useNearest,
  usePersistedFlag,
} from '@/components/map/NearbyLayers';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileSignature, Plus, Edit, Trash2, Search, LayoutGrid, List, Map as MapIcon, MapPin, User, Calendar, Clock, CheckCircle2, AlertTriangle, ExternalLink, X, Eye, Upload, FileText, Package, Maximize2, Home, Trash, Footprints, Baby, Dumbbell, Droplet, Droplets, BarChart3, Sprout, Navigation } from 'lucide-react';
import { LightPoleIcon, BenchIcon } from "@/components/icons/InventoryIcons";
import { toast } from "sonner";

// Leaflet varsayılan marker ikonları bundler altında çözülmez → kırık "Marker"
// alt metni görünür. CDN ikon yollarını bir kez tanımla (MapDisplay.jsx ile aynı).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function isCanceledError(error) {
  if (!error) return false;
  if (axios.isCancel?.(error)) return true;
  if (error.code === "ERR_CANCELED") return true;
  if (typeof error.message === "string" && error.message.toLowerCase() === "canceled") return true;
  return false;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Protokollü park haritasında açılıp kapanan referans katmanları.
const NEARBY_LAYER_KEYS = ['park', 'pasif'];

// Protokol durum görselleştirmesi (backend _derive_protocol_status ile uyumlu)
const PROTOCOL_STATUS = {
  active: { label: 'Aktif', className: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle2 },
  expiring: { label: 'Süresi Yaklaşıyor', className: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  expired: { label: 'Süresi Doldu', className: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle },
  renewed: { label: 'Yenilendi', className: 'bg-blue-100 text-blue-700 border-blue-300', icon: CheckCircle2 },
};

function getStatusInfo(status) {
  return PROTOCOL_STATUS[status] || PROTOCOL_STATUS.active;
}

// Envanter Özeti kategorileri — ana sayfa Envanter Özeti (InventorySummaryCards)
// ile birebir aynı anahtar, etiket, ikon ve renkler. Modal girişi, istatistik
// kartları ve özet hep bu listeyi kullanır.
const INVENTORY_CATEGORIES = [
  { key: 'light_pole_count', label: 'Elektrik Direği', Icon: LightPoleIcon, bg: 'bg-yellow-50 hover:bg-yellow-100', value: 'text-yellow-900', sub: 'text-yellow-700', icon: 'text-yellow-600' },
  { key: 'bench_count', label: 'Bank', Icon: BenchIcon, bg: 'bg-orange-50 hover:bg-orange-100', value: 'text-orange-900', sub: 'text-orange-700', icon: 'text-orange-600' },
  { key: 'trash_bin_count', label: 'Çöp Kovası', Icon: Trash, bg: 'bg-gray-50 hover:bg-gray-100', value: 'text-gray-900', sub: 'text-gray-700', icon: 'text-gray-600' },
  { key: 'gazebo_count', label: 'Kamelya', Icon: Home, bg: 'bg-blue-50 hover:bg-blue-100', value: 'text-blue-900', sub: 'text-blue-700', icon: 'text-blue-600' },
];

// Tesis Durumu — ana sayfadaki "Tesis Durumu" kartıyla aynı öğeler.
const FACILITY_ITEMS = [
  { key: 'has_running_track', label: 'Yürüme/Koşu Yolu', Icon: Footprints, color: 'text-blue-600', hover: 'hover:bg-blue-50' },
  { key: 'has_playground', label: 'Çocuk Oyun Grubu', Icon: Baby, color: 'text-purple-600', hover: 'hover:bg-purple-50' },
  { key: 'has_sports_equipment', label: 'Spor Aleti', Icon: Dumbbell, color: 'text-orange-600', hover: 'hover:bg-orange-50' },
  { key: 'has_fountain', label: 'Havuz', Icon: Droplet, color: 'text-blue-400', hover: 'hover:bg-blue-50' },
];

// Spor Sahaları — ana sayfadaki emoji görselleriyle birebir aynı.
const SPORTS_ITEMS = [
  { key: 'has_basketball_court', label: 'Basketbol Sahası', emoji: '🏀', hover: 'hover:bg-orange-50' },
  { key: 'has_football_field', label: 'Futbol Sahası', emoji: '⚽', hover: 'hover:bg-green-50' },
  { key: 'has_volleyball_court', label: 'Voleybol Sahası', emoji: '🏐', hover: 'hover:bg-blue-50' },
  { key: 'has_tennis_court', label: 'Tenis Kortu', emoji: '🎾', hover: 'hover:bg-yellow-50' },
];

// Sulama Sistemi Dağılımı — ana sayfadaki değerler/renklerle aynı.
const IRRIGATION_ITEMS = [
  { key: 'otomatik', label: 'Otomatik Sulama', badge: 'bg-green-100 text-green-700', bar: 'bg-green-500', hover: 'hover:bg-green-50' },
  { key: 'manuel', label: 'Manuel Sulama', badge: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500', hover: 'hover:bg-blue-50' },
  { key: 'hortumla', label: 'Hortumla Sulama', badge: 'bg-yellow-100 text-yellow-700', bar: 'bg-yellow-500', hover: 'hover:bg-yellow-50' },
  { key: 'yok', label: 'Sulama Yok', badge: 'bg-gray-100 text-gray-700', bar: 'bg-gray-400', hover: 'hover:bg-gray-50' },
];

// Sekme içi alt görünümler — dinamik ve yetkiye bağlı yapı.
// Görünümler sabit butonlar yerine bu listeden türetilir; her görünüm bir
// yetki slug'ına bağlıdır. Kullanıcının (hiyerarşik rolünden gelen) dinamik
// yetkisi yoksa o görünüm sekmede hiç oluşturulmaz. Yeni bir alt görünüm
// eklemek için bu listeye satır eklemek yeterlidir.
const PROTOKOL_VIEWS = [
  { key: 'overview', label: 'Genel Bakış', Icon: BarChart3, permission: 'protokol_park.gor' },
  { key: 'parks', label: 'Protokollü Parklar', Icon: FileSignature, permission: 'protokol_park.gor' },
];

// Protokol Parkları listesinin görüntüleme modları — kart / tablo / harita.
// Pasif Alanlar sekmesiyle aynı yapı. Geçiş çubuğu bu listeden dinamik üretilir.
const PARK_VIEW_MODES = [
  { key: 'cards', label: 'Kart', Icon: LayoutGrid },
  { key: 'table', label: 'Tablo', Icon: List },
  { key: 'map', label: 'Harita', Icon: MapIcon },
];

// Haritada konum seçimi
function LocationMarker({ onLocationSelect }) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Haritada sınır noktası ekleme
function BoundaryMarker({ onPointAdd }) {
  useMapEvents({
    click(e) {
      onPointAdd(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const EMPTY_FORM = {
  name: '',
  neighborhood: '',
  address: '',
  latitude: null,
  longitude: null,
  boundaries: null,
  area: null,
  grass_area: null,
  bench_count: 0,
  gazebo_count: 0,
  light_pole_count: 0,
  trash_bin_count: 0,
  has_running_track: false,
  has_playground: false,
  has_sports_equipment: false,
  has_fountain: false,
  has_basketball_court: false,
  has_football_field: false,
  has_volleyball_court: false,
  has_tennis_court: false,
  irrigation_system: 'yok',
  protocol_no: '',
  protocol_date: '',
  protocol_end_date: '',
  counterparty_name: '',
  counterparty_contact: '',
  protocol_documents: [],
  region_id: '',
  assigned_formen_id: '',
  assigned_bolge_muhendisi_id: '',
  assigned_mudur_yardimcisi_id: '',
  notes: '',
};

export default function ProtokolParklarTab({
  userRole,
  personnelId,
  personnel = [],
  regions = [],
  API: apiProp,
  showInventoryDetail,
  facilityDetailHandlers,
  showFacilityDetail,
  showIrrigationDetail,
}) {
  const flagsCtx = useRoleFlags(userRole);
  const { has } = flagsCtx;

  const apiUrl = apiProp || API;

  const [parks, setParks] = useState([]);
  const [mapLayer, setMapLayer] = useMapLayer("street"); // Harita / Uydu (görünüm haritası)
  // Harita yakınlık katmanları (park / pasif alan) — seçim tarayıcıda kalıcı.
  // Taban katman (protokol parkları). Kapatmak yalnız o anlık bir bakıştır —
  // kalıcı DEĞİL: haritaya her girişte yeniden işaretli gelir.
  const [showProtokolLayer, setShowProtokolLayer] = useState(true);
  const [showParksLayer, setShowParksLayer] = usePersistedFlag('protokolParkMap.showParks', false);
  const [showPasifLayer, setShowPasifLayer] = usePersistedFlag('protokolParkMap.showPasif', false);
  const [linkLine, setLinkLine] = useState(null);   // [[parkLat,parkLng],[refLat,refLng]]
  const [linkInfo, setLinkInfo] = useState(null);   // { refName, distance }
  const [formMapLayer, setFormMapLayer] = useMapLayer("street"); // Harita / Uydu (sınır çizim haritası)
  const [formSearchCenter, setFormSearchCenter] = useState(null); // Adres aramada bulunan konum
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFormenId, setFilterFormenId] = useState("all");
  const [viewMode, setViewMode] = useState("cards");
  // Aktif alt görünüm — başlangıç değeri yetki filtresinden sonra düzeltilir.
  const [activeView, setActiveView] = useState(PROTOKOL_VIEWS[0]?.key);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFormReadOnly, setIsFormReadOnly] = useState(false);
  const [selectedPark, setSelectedPark] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parkToDelete, setParkToDelete] = useState(null);
  // Silme ucuncu onayi: kullanicinin elle yazdigi park adi. Tek tikla onaylanan
  // diyalog kazara silmeyi engelleyemiyordu.
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  // İstatistik kırılım modalı — { title, rows: [{ park, extra? }] }
  const [detail, setDetail] = useState(null);
  // Kırılım modalı içindeki park adı araması (ana sayfa envanter modalıyla aynı davranış)
  const [detailQuery, setDetailQuery] = useState('');

  const [form, setForm] = useState(EMPTY_FORM);
  const [mapCenter, setMapCenter] = useState([39.925533, 32.866287]); // Ankara
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawnPoints, setDrawnPoints] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Yetki kapıları (admin panel Rol Yönetimi'nden anında değişir)
  const canCreate = has('protokol_park.olustur');
  const canEdit = has('protokol_park.duzenle');
  const canDelete = has('protokol_park.sil');
  const canSeeAllScope = has('protokol_park.tum_kapsam');
  const canView = has('protokol_park.gor');

  // Kullanıcının dinamik yetkisine göre erişilebilen alt görünümler.
  // Yetki, admin Rol Yönetimi'nden anında değişebileceği için her render'da
  // yeniden hesaplanır; yetkisi olmayan görünüm sekmede hiç gösterilmez.
  const availableViews = useMemo(
    () => PROTOKOL_VIEWS.filter(v => has(v.permission)),
    [has],
  );

  // Aktif görünüm artık erişilebilir değilse (yetki kalkmışsa) ilk
  // erişilebilir görünüme düş.
  useEffect(() => {
    if (availableViews.length > 0 && !availableViews.some(v => v.key === activeView)) {
      setActiveView(availableViews[0].key);
    }
  }, [availableViews, activeView]);

  const formens = useMemo(() => personnel.filter(p => p.role === 'formen'), [personnel]);
  const bolgeMuhendisleri = useMemo(() => personnel.filter(p => p.role === 'bolge_muhendisi'), [personnel]);
  const mudurYardimcilari = useMemo(() => personnel.filter(p => p.role === 'mudur_yardimcisi'), [personnel]);

  const fetchParks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/protokol-parklar`);
      setParks(response.data || []);
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Protokol parkları yüklenirken hata:", error);
      toast.error("Protokol parkları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchParks();
  }, [fetchParks]);

  // Backend zaten kapsam (scope) filtresi uyguluyor; burada sadece UI filtreleri
  const visibleRegions = useMemo(() => {
    const regionIdsWithParks = new Set(parks.map(p => p.region_id).filter(Boolean));
    if (canSeeAllScope) return regions;
    return regions.filter(r => regionIdsWithParks.has(r.id));
  }, [regions, parks, canSeeAllScope]);

  const visibleFormens = useMemo(() => {
    return [...new Map(
      parks
        .filter(p => p.assigned_formen_id && p.assigned_formen_name)
        .map(p => [p.assigned_formen_id, { id: p.assigned_formen_id, name: p.assigned_formen_name }])
    ).values()].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
  }, [parks]);

  const filteredParks = useMemo(() => {
    const term = normalizeSearchText(searchTerm);
    return parks.filter(park => {
      const matchesSearch = !term ||
        normalizeSearchText(park.name).includes(term) ||
        normalizeSearchText(park.neighborhood).includes(term) ||
        normalizeSearchText(park.address).includes(term) ||
        normalizeSearchText(park.counterparty_name).includes(term) ||
        normalizeSearchText(park.protocol_no).includes(term);
      const matchesRegion = filterRegion === 'all' || park.region_id === filterRegion;
      const matchesStatus = filterStatus === 'all' || park.protocol_status === filterStatus;
      const matchesFormen = filterFormenId === 'all' || park.assigned_formen_id === filterFormenId;
      return matchesSearch && matchesRegion && matchesStatus && matchesFormen;
    }).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
  }, [parks, searchTerm, filterRegion, filterStatus, filterFormenId]);

  // İstatistik + envanter özeti — kapsam (backend) ve UI filtrelerinden geçmiş
  // `filteredParks` üzerinden toplanır; böylece hiyerarşik kapsamla uyumludur.
  const stats = useMemo(() => {
    const s = {
      total: filteredParks.length,
      active: 0, expiring: 0, expired: 0, renewed: 0,
      totalArea: 0,
      totalGrassArea: 0,
      totalDocuments: 0,
      // Envanter toplamları (ana sayfa Envanter Özeti ile aynı kategoriler)
      inventory: Object.fromEntries(INVENTORY_CATEGORIES.map(c => [c.key, 0])),
      // Tesis / spor sahası — kaç parkta var
      facilities: Object.fromEntries(FACILITY_ITEMS.map(f => [f.key, 0])),
      sports: Object.fromEntries(SPORTS_ITEMS.map(f => [f.key, 0])),
      // Sulama sistemi dağılımı
      irrigation: { otomatik: 0, manuel: 0, hortumla: 0, yok: 0 },
    };
    filteredParks.forEach(p => {
      if (s[p.protocol_status] !== undefined) s[p.protocol_status] += 1;
      s.totalArea += Number(p.area) || 0;
      s.totalGrassArea += Number(p.grass_area) || 0;
      s.totalDocuments += (p.protocol_documents || []).length;
      INVENTORY_CATEGORIES.forEach(c => { s.inventory[c.key] += Number(p[c.key]) || 0; });
      FACILITY_ITEMS.forEach(f => { if (p[f.key]) s.facilities[f.key] += 1; });
      SPORTS_ITEMS.forEach(f => { if (p[f.key]) s.sports[f.key] += 1; });
      const irr = p.irrigation_system || 'yok';
      if (s.irrigation[irr] !== undefined) s.irrigation[irr] += 1;
      else s.irrigation.yok += 1;
    });
    return s;
  }, [filteredParks]);

  const getRegionName = useCallback((regionId) => {
    return regions.find(r => r.id === regionId)?.name || '-';
  }, [regions]);

  // --- Harita yakınlık katmanları (park / pasif alan) -----------------------

  // Haritada gösterilebilir (koordinatlı) protokol parkları.
  const mapParks = useMemo(
    () => filteredParks
      .map(p => ({ ...p, _lat: toCoord(p.latitude), _lng: toCoord(p.longitude) }))
      .filter(p => p._lat !== null && p._lng !== null),
    [filteredParks]
  );

  const { points: nearbyPoints, loading: loadingNearby } = useNearbyPoints(
    apiUrl,
    NEARBY_LAYER_KEYS,
    activeView === 'parks' && viewMode === 'map'
  );

  // Harita her açıldığında taban katmanı geri aç (kart/tablo ya da başka
  // sekmeye gidip dönünce kutucuk boş kalmasın).
  useEffect(() => {
    if (activeView === 'parks' && viewMode === 'map') setShowProtokolLayer(true);
  }, [activeView, viewMode]);

  const parkPoints = useMemo(() => nearbyPoints.park || [], [nearbyPoints.park]);
  const pasifPoints = useMemo(() => nearbyPoints.pasif || [], [nearbyPoints.pasif]);

  // Mesafe hesabı tüm referanslar üzerinden; katman kapalıyken de "en yakın" çalışır.
  const referencePoints = useMemo(
    () => [...parkPoints, ...pasifPoints],
    [parkPoints, pasifPoints]
  );

  // Katman seçimine göre haritada çizilecek referanslar.
  const visibleReferences = useMemo(() => {
    const rows = [];
    if (showParksLayer) rows.push(...parkPoints);
    if (showPasifLayer) rows.push(...pasifPoints);
    return rows;
  }, [showParksLayer, showPasifLayer, parkPoints, pasifPoints]);

  // Her protokol parkı için en yakın 3 park/pasif alan (kuş uçuşu).
  const nearestOrigins = useMemo(
    () => mapParks.map(p => ({ id: p.id, kind: 'protokol', lat: p._lat, lng: p._lng })),
    [mapParks]
  );
  const nearestByParkId = useNearest(nearestOrigins, referencePoints);

  // Otomatik çerçeveleme: bağlantı çizgisi varsa iki noktaya, yoksa tüm parklara.
  const mapFitBounds = linkLine || mapParks.map(p => [p._lat, p._lng]);
  const mapFitKey = linkLine
    ? `link:${JSON.stringify(linkLine)}`
    : `parks:${mapParks.map(p => `${p.id}:${p._lat},${p._lng}`).join('|')}`;

  // "En yakın" listesinden bir referansa tıklanınca: katmanı aç, çizgiyi kur.
  const focusReference = (park, ref) => {
    if (ref.kind === 'park') setShowParksLayer(true);
    else setShowPasifLayer(true);
    setLinkLine([[park._lat, park._lng], [ref.lat, ref.lng]]);
    setLinkInfo({ refName: ref.name, distance: ref.distance });
  };

  const clearLink = () => {
    setLinkLine(null);
    setLinkInfo(null);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setDrawnPoints([]);
    setDrawingMode(false);
    setMapCenter([39.925533, 32.866287]);
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(false);
    setIsFormReadOnly(false);
    setSelectedPark(null);
    setIsDialogOpen(true);
  };

  const populateForm = (park) => {
    setForm({
      name: park.name || '',
      neighborhood: park.neighborhood || '',
      address: park.address || '',
      latitude: park.latitude,
      longitude: park.longitude,
      boundaries: park.boundaries,
      area: park.area,
      grass_area: park.grass_area,
      bench_count: park.bench_count || 0,
      gazebo_count: park.gazebo_count || 0,
      light_pole_count: park.light_pole_count || 0,
      trash_bin_count: park.trash_bin_count || 0,
      has_running_track: !!park.has_running_track,
      has_playground: !!park.has_playground,
      has_sports_equipment: !!park.has_sports_equipment,
      has_fountain: !!park.has_fountain,
      has_basketball_court: !!park.has_basketball_court,
      has_football_field: !!park.has_football_field,
      has_volleyball_court: !!park.has_volleyball_court,
      has_tennis_court: !!park.has_tennis_court,
      irrigation_system: park.irrigation_system || 'yok',
      protocol_no: park.protocol_no || '',
      protocol_date: park.protocol_date || '',
      protocol_end_date: park.protocol_end_date || '',
      counterparty_name: park.counterparty_name || '',
      counterparty_contact: park.counterparty_contact || '',
      protocol_documents: park.protocol_documents || [],
      region_id: park.region_id || '',
      assigned_formen_id: park.assigned_formen_id || '',
      assigned_bolge_muhendisi_id: park.assigned_bolge_muhendisi_id || '',
      assigned_mudur_yardimcisi_id: park.assigned_mudur_yardimcisi_id || '',
      notes: park.notes || '',
    });
    setDrawnPoints(park.boundaries || []);
    if (park.latitude && park.longitude) {
      setMapCenter([park.latitude, park.longitude]);
    }
    setSelectedPark(park);
    setIsDialogOpen(true);
  };

  const handleEdit = (park) => {
    setIsEditing(true);
    setIsFormReadOnly(false);
    setDrawingMode(false);
    populateForm(park);
  };

  const handleView = (park) => {
    setIsEditing(true);
    setIsFormReadOnly(true);
    setDrawingMode(false);
    populateForm(park);
  };

  const handleLocationSelect = (lat, lng) => {
    setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
    setMapCenter([lat, lng]);
  };

  const handleBoundaryPointAdd = (lat, lng) => {
    if (drawingMode) {
      setDrawnPoints(prev => [...prev, [lat, lng]]);
    }
  };

  const calculatePolygonArea = (points) => {
    if (points.length < 3) return 0;
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][1] * points[j][0];
      area -= points[j][1] * points[i][0];
    }
    area = Math.abs(area) / 2;
    return Math.round(area * 111319.9 * 111319.9 * Math.cos(points[0][0] * Math.PI / 180));
  };

  const handleSaveBoundaries = () => {
    if (drawnPoints.length >= 3) {
      const calculatedArea = calculatePolygonArea(drawnPoints);
      setForm(prev => ({ ...prev, boundaries: drawnPoints, area: calculatedArea }));
      setDrawingMode(false);
      toast.success("Sınırlar kaydedildi");
    } else {
      toast.error("En az 3 nokta gerekli");
    }
  };

  const handleClearBoundaries = () => {
    setDrawnPoints([]);
    setForm(prev => ({ ...prev, boundaries: null, area: null }));
  };

  const handleCountChange = (key, value) => {
    const n = value === '' ? 0 : Math.max(0, parseInt(value, 10) || 0);
    setForm(prev => ({ ...prev, [key]: n }));
  };

  const handleToggle = (key) => {
    setForm(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // İstatistik kırılım modalını aç. `predicate` ile `filteredParks` süzülür
  // (yani backend kapsamı + UI filtreleri zaten uygulanmıştır → hiyerarşi
  // korunur). `extra` opsiyonel olarak park başına bir değer (örn. "12 adet")
  // gösterir. Eşleşen park yoksa modal açılmaz.
  const showDetail = (title, predicate, extra = null) => {
    const rows = filteredParks
      .filter(predicate)
      .map(park => ({ park, extra: extra ? extra(park) : null }));
    if (rows.length === 0) {
      toast.info('Bu kritere uyan protokol park bulunamadı');
      return;
    }
    setDetailQuery(''); // önceki modalda kalan arama yeni listeyi boş göstermesin
    setDetail({ title, rows });
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const response = await axios.post(`${apiUrl}/api/upload-protokol-evrak`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = response.data?.url;
      if (url) {
        setForm(prev => ({ ...prev, protocol_documents: [...(prev.protocol_documents || []), url] }));
        toast.success("Evrak yüklendi");
      }
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Evrak yükleme hatası:", error);
      toast.error(error.response?.data?.detail || "Evrak yüklenemedi");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveDocument = (url) => {
    setForm(prev => ({
      ...prev,
      protocol_documents: (prev.protocol_documents || []).filter(d => d !== url),
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("Protokol park adı zorunludur");
      return;
    }
    if (form.protocol_date && form.protocol_end_date && form.protocol_end_date < form.protocol_date) {
      toast.error("Bitiş tarihi başlangıç tarihinden önce olamaz");
      return;
    }
    try {
      const payload = {
        ...form,
        boundaries: drawnPoints.length >= 3 ? drawnPoints : form.boundaries,
      };
      if (isEditing && selectedPark) {
        await axios.put(`${apiUrl}/api/protokol-parklar/${selectedPark.id}`, payload);
        toast.success("Protokol park güncellendi");
      } else {
        await axios.post(`${apiUrl}/api/protokol-parklar`, payload);
        toast.success("Protokol park oluşturuldu");
      }
      setIsDialogOpen(false);
      fetchParks();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Protokol park kaydedilirken hata:", error);
      toast.error(error.response?.data?.detail || "Kaydetme sırasında hata oluştu");
    }
  };

  const handleDelete = async () => {
    // Ucuncu onay: butona ulasilamasa bile istek adi yazilmadan gitmez.
    if (!parkToDelete || !deleteConfirmed) return;
    try {
      await axios.delete(`${apiUrl}/api/protokol-parklar/${parkToDelete.id}`);
      toast.success("Protokol park silindi");
      setDeleteDialogOpen(false);
      setParkToDelete(null);
      fetchParks();
    } catch (error) {
      if (isCanceledError(error)) return;
      console.error("Protokol park silinirken hata:", error);
      toast.error("Silme sırasında hata oluştu");
    }
  };

  // Diyalog her acilip kapandiginda yazilan onay metni sifirlanir; aksi halde
  // onceki parkin adi kutuda kalip onayi anlamsizlastirirdi.
  useEffect(() => {
    setDeleteConfirmText('');
  }, [parkToDelete, deleteDialogOpen]);

  const deleteConfirmed =
    !!parkToDelete && deleteNameMatches(deleteConfirmText, parkToDelete.name);

  const docUrl = (url) => (url?.startsWith('http') ? url : `${apiUrl}${url}`);

  // Bitiş tarihine kalan gün sayısı (negatif = geçmiş). Tarih yoksa null.
  const daysUntil = (endDate) => {
    if (!endDate) return null;
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(end.getTime())) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((end - today) / 86400000);
  };

  // Bitiş geri sayımını metin + renk olarak döndürür (kart/tablo/modal için
  // ortak). Backend eşiği 30 gün (PROTOCOL_EXPIRING_THRESHOLD_DAYS) ile uyumlu.
  const expiryInfo = (endDate) => {
    const d = daysUntil(endDate);
    if (d === null) return null;
    if (d < 0) return { days: d, text: `${Math.abs(d)} gün önce doldu`, cls: 'text-red-600' };
    if (d === 0) return { days: 0, text: 'Bugün doluyor', cls: 'text-red-600 font-medium' };
    if (d <= 30) return { days: d, text: `Bitişe ${d} gün kaldı`, cls: 'text-amber-600 font-medium' };
    return { days: d, text: `Bitişe ${d} gün`, cls: 'text-gray-500' };
  };

  const StatusBadge = ({ status }) => {
    const info = getStatusInfo(status);
    const Icon = info.icon;
    return (
      <Badge variant="outline" className={`gap-1 ${info.className}`}>
        <Icon className="h-3 w-3" />
        {info.label}
      </Badge>
    );
  };

  // Üst özet kartları — dinamik liste. Her kart tıklanabilir; tıklanınca o
  // kritere uyan parklar kırılım modalında (nerede oldukları ile) listelenir.
  const summaryCards = [
    { key: 'total', label: 'Protokollü Toplam Park', value: stats.total, Icon: FileSignature, box: 'bg-indigo-100', color: 'text-indigo-600',
      onClick: () => showDetail('Tüm Protokollü Parklar', () => true) },
    { key: 'area', label: 'Protokollü Parkların Toplam Alanı (m²)', value: Math.round(stats.totalArea).toLocaleString('tr-TR'), Icon: Maximize2, box: 'bg-sky-100', color: 'text-sky-600',
      onClick: () => showDetail('Alanı girilmiş parklar', p => Number(p.area) > 0, p => `${Number(p.area).toLocaleString('tr-TR')} m²`) },
    { key: 'grass', label: 'Protokollü Parkların Çim Alanı (m²)', value: Math.round(stats.totalGrassArea).toLocaleString('tr-TR'), Icon: Sprout, box: 'bg-green-100', color: 'text-green-600',
      onClick: () => showDetail('Çim alanı girilmiş parklar', p => Number(p.grass_area) > 0, p => `${Number(p.grass_area).toLocaleString('tr-TR')} m²`) },
    { key: 'active', label: 'Aktif', value: stats.active, Icon: CheckCircle2, box: 'bg-green-100', color: 'text-green-600',
      onClick: () => showDetail('Aktif protokollü parklar', p => p.protocol_status === 'active') },
    { key: 'expiring', label: 'Protokol Süresi Yaklaşan', value: stats.expiring, Icon: Clock, box: 'bg-amber-100', color: 'text-amber-600',
      onClick: () => showDetail('Süresi yaklaşan parklar', p => p.protocol_status === 'expiring') },
    { key: 'expired', label: 'Protokol Süresi Dolan', value: stats.expired, Icon: AlertTriangle, box: 'bg-red-100', color: 'text-red-600',
      onClick: () => showDetail('Süresi dolmuş parklar', p => p.protocol_status === 'expired') },
    { key: 'docs', label: 'Evrak', value: stats.totalDocuments, Icon: FileText, box: 'bg-gray-100', color: 'text-gray-600',
      onClick: () => showDetail('Evrakı olan parklar', p => (p.protocol_documents || []).length > 0, p => `${(p.protocol_documents || []).length} evrak`) },
  ];

  return (
    <TabsContent value="protokol-parks" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSignature className="h-8 w-8 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold">Protokollü Parklar</h2>
            <p className="text-gray-500">Belediyece yapılıp site yönetimine protokolle devredilen parklar</p>
          </div>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {filteredParks.length} / {parks.length} Park
        </Badge>
      </div>

      {/* Alt sekme geçişi — PROTOKOL_VIEWS listesinden dinamik üretilir ve
          yalnızca kullanıcının yetkisi olan görünümler gösterilir. Tek görünüm
          erişilebilirse geçiş çubuğu hiç çizilmez. */}
      {availableViews.length > 1 && (
        <div className="inline-flex rounded-lg border bg-gray-50 p-1">
          {availableViews.map(view => {
            const Icon = view.Icon;
            const active = activeView === view.key;
            return (
              <Button
                key={view.key}
                variant={active ? 'default' : 'ghost'}
                size="sm"
                className={active ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
                onClick={() => setActiveView(view.key)}
              >
                <Icon className="h-4 w-4 mr-2" />
                {view.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* ===================== GENEL BAKIŞ ===================== */}
      {activeView === 'overview' && (
        <>
          {!canView || loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {[1, 2, 3, 4, 5, 6, 7].map(i => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="pt-5 pb-4 h-[68px]" />
                </Card>
              ))}
            </div>
          ) : (
      <>
      {/* İstatistik kartları + envanter/tesis/sulama özeti (ana sayfa
          istatistikleriyle birebir aynı düzen) */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {summaryCards.map(c => {
          const Icon = c.Icon;
          return (
            <Card
              key={c.key}
              role="button"
              tabIndex={0}
              title="Detayları gör"
              onClick={c.onClick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); c.onClick(); } }}
              className="cursor-pointer hover:shadow-md hover:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-all"
            >
              <CardContent className="pt-5 pb-4 flex items-center gap-3">
                <div className={`rounded-lg ${c.box} p-2`}><Icon className={`h-5 w-5 ${c.color}`} /></div>
                <div>
                  <p className="text-2xl font-bold leading-none">{c.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{c.label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Envanter Özeti — ana sayfayla aynı görseller */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold">Protokollü Parkların Envanter Özeti</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">Protokollü parklardaki envanter toplamları</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {INVENTORY_CATEGORIES.map(cat => {
              const Icon = cat.Icon;
              return (
                <button
                  key={cat.key}
                  type="button"
                  title="Bu envantere sahip parkları gör"
                  onClick={() => (showInventoryDetail
                    ? showInventoryDetail(cat.label, cat.key, filteredParks)
                    : showDetail(`${cat.label} bulunan parklar`, p => Number(p[cat.key]) > 0, p => `${Number(p[cat.key]).toLocaleString('tr-TR')} adet`))}
                  className={`text-center p-4 rounded-lg ${cat.bg} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                >
                  <Icon className={`h-8 w-8 ${cat.icon} mx-auto mb-2`} />
                  <p className={`text-2xl font-bold ${cat.value}`}>{(stats.inventory[cat.key] || 0).toLocaleString('tr-TR')}</p>
                  <p className={`text-xs ${cat.sub}`}>{cat.label}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tesis Durumu */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <Dumbbell className="h-5 w-5 text-orange-600" />
              <h3 className="font-semibold">Tesis Durumu</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">Protokol parklardaki tesislerin dağılımı</p>
            <div className="space-y-3">
              {FACILITY_ITEMS.map(item => {
                const Icon = item.Icon;
                return (
                  <button
                    key={item.key}
                    type="button"
                    title="Bu tesise sahip parkları gör"
                    onClick={() => (facilityDetailHandlers?.[item.key]
                      ? facilityDetailHandlers[item.key](filteredParks)
                      : showDetail(`${item.label} olan parklar`, p => !!p[item.key]))}
                    className={`w-full flex items-center justify-between p-2 rounded ${item.hover} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${item.color}`} />
                      <span className="text-sm">{item.label}</span>
                    </div>
                    <Badge variant="secondary">{stats.facilities[item.key]} park</Badge>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Spor Sahaları */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <Dumbbell className="h-5 w-5 text-orange-600" />
              <h3 className="font-semibold">Spor Sahaları</h3>
            </div>
            <p className="text-sm text-gray-500 mb-3">Protokol parklardaki spor tesisleri</p>
            <div className="space-y-3">
              {SPORTS_ITEMS.map(item => (
                <button
                  key={item.key}
                  type="button"
                  title="Bu spor sahasına sahip parkları gör"
                  onClick={() => (showFacilityDetail
                    ? showFacilityDetail(item.label, item.key, filteredParks)
                    : showDetail(`${item.label} olan parklar`, p => !!p[item.key]))}
                  className={`w-full flex items-center justify-between p-2 rounded ${item.hover} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{item.emoji}</span>
                    <span className="text-sm">{item.label}</span>
                  </div>
                  <Badge variant="secondary">{stats.sports[item.key]} park</Badge>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sulama Sistemi Dağılımı */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-1">
            <Droplets className="h-5 w-5 text-blue-500" />
            <h3 className="font-semibold">Sulama Sistemi Dağılımı</h3>
          </div>
          <p className="text-sm text-gray-500 mb-4">Protokol parklardaki sulama yöntemleri</p>
          <div className="space-y-4">
            {IRRIGATION_ITEMS.map(item => {
              const count = stats.irrigation[item.key] || 0;
              const pct = stats.total > 0 ? (count / stats.total * 100).toFixed(0) : 0;
              return (
                <button
                  key={item.key}
                  type="button"
                  title="Bu sulama yöntemini kullanan parkları gör"
                  onClick={() => (showIrrigationDetail
                    ? showIrrigationDetail(`${item.label} olan parklar`, item.key, filteredParks)
                    : showDetail(`${item.label} olan parklar`, p => (p.irrigation_system || 'yok') === item.key))}
                  className={`w-full text-left space-y-2 p-3 rounded-lg ${item.hover} transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400`}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{item.label}</span>
                    <Badge className={item.badge}>{count} park</Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`${item.bar} h-2 rounded-full`} style={{ width: `${pct}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
      </>
          )}
        </>
      )}

      {/* ===================== PROTOKOL PARKLARI (liste) ===================== */}
      {activeView === 'parks' && (
        <>
      {/* Filters & Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Ara (ad, mahalle, adres, site, protokol no)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {visibleRegions.length > 0 && (
              <SearchableSelect
                value={filterRegion}
                onChange={setFilterRegion}
                className="w-[170px]"
                allLabel="Tüm Bölgeler"
                allValue="all"
                searchPlaceholder="Bölge ara..."
                options={visibleRegions.map(region => ({ id: region.id, name: region.name }))}
              />
            )}

            <SearchableSelect
              value={filterStatus}
              onChange={setFilterStatus}
              className="w-[170px]"
              allLabel="Tüm Durumlar"
              allValue="all"
              searchPlaceholder="Durum ara..."
              options={[
                { id: "active", name: "Aktif" },
                { id: "expiring", name: "Süresi Yaklaşıyor" },
                { id: "expired", name: "Süresi Doldu" },
                { id: "renewed", name: "Yenilendi" },
              ]}
            />

            {canSeeAllScope && visibleFormens.length > 0 && (
              <SearchableSelect
                value={filterFormenId}
                onChange={setFilterFormenId}
                className="w-[170px]"
                allLabel="Tüm Formenler"
                allValue="all"
                searchPlaceholder="Formen ara..."
                options={visibleFormens.map(f => ({ id: f.id, name: f.name }))}
              />
            )}

            {/* Görüntüleme modu geçişi — PARK_VIEW_MODES'tan dinamik üretilir */}
            <div className="flex border rounded-lg">
              {PARK_VIEW_MODES.map(mode => {
                const Icon = mode.Icon;
                return (
                  <Button
                    key={mode.key}
                    variant={viewMode === mode.key ? 'default' : 'ghost'}
                    size="sm"
                    title={mode.label}
                    onClick={() => setViewMode(mode.key)}
                  >
                    <Icon className="h-4 w-4" />
                  </Button>
                );
              })}
            </div>

            {canCreate && (
              <Button onClick={handleAddNew} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-2" />
                Yeni Protokollü Park
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="space-y-3 pt-6">
                <div className="h-5 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredParks.length === 0 ? (
        <Card className="p-12 text-center">
          <FileSignature className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">Protokol park bulunamadı</h3>
          {canCreate && (
            <p className="text-gray-500 mt-2">Yeni bir protokol park eklemek için yukarıdaki butonu kullanın.</p>
          )}
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredParks.map(park => {
            const clickable = !canEdit && canView;
            return (
              <Card
                key={park.id}
                className={`hover:shadow-lg transition-shadow ${clickable ? 'cursor-pointer' : ''}`}
                onClick={() => { if (clickable) handleView(park); }}
              >
                <CardContent className="pt-6 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-lg leading-tight">{park.name}</h3>
                    <StatusBadge status={park.protocol_status} />
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    {park.protocol_no && (
                      <p className="flex items-center gap-1"><FileSignature className="h-3.5 w-3.5" />Protokol No: <span className="font-medium">{park.protocol_no}</span></p>
                    )}
                    {park.neighborhood && (
                      <p className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{park.neighborhood}</p>
                    )}
                    <p>Bölge: <span className="font-medium">{getRegionName(park.region_id)}</span></p>
                    {park.counterparty_name && (
                      <p>Site Yönetimi: <span className="font-medium">{park.counterparty_name}</span></p>
                    )}
                    {park.protocol_end_date && (
                      <p className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Bitiş: {park.protocol_end_date}</p>
                    )}
                    {park.protocol_end_date && (() => {
                      const info = expiryInfo(park.protocol_end_date);
                      return info ? (
                        <p className={`flex items-center gap-1 ${info.cls}`}><Clock className="h-3.5 w-3.5" />{info.text}</p>
                      ) : null;
                    })()}
                    {park.assigned_formen_name && (
                      <p className="flex items-center gap-1"><User className="h-3.5 w-3.5" />Formen: {park.assigned_formen_name}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
                    {canEdit ? (
                      <Button variant="outline" size="sm" onClick={() => handleEdit(park)}>
                        <Edit className="h-4 w-4 mr-1" /> Düzenle
                      </Button>
                    ) : canView ? (
                      <Button variant="outline" size="sm" onClick={() => handleView(park)}>
                        <Eye className="h-4 w-4 mr-1" /> Görüntüle
                      </Button>
                    ) : null}
                    {canDelete && (
                      <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => { setParkToDelete(park); setDeleteDialogOpen(true); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : viewMode === 'table' ? (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad</TableHead>
                  <TableHead>Bölge</TableHead>
                  <TableHead>Site Yönetimi</TableHead>
                  <TableHead>Bitiş</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Formen</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredParks.map(park => (
                  <TableRow key={park.id}>
                    <TableCell className="font-medium">{park.name}</TableCell>
                    <TableCell>{getRegionName(park.region_id)}</TableCell>
                    <TableCell>{park.counterparty_name || '-'}</TableCell>
                    <TableCell>
                      {park.protocol_end_date ? (
                        <div>
                          <span>{park.protocol_end_date}</span>
                          {(() => {
                            const info = expiryInfo(park.protocol_end_date);
                            return info ? <p className={`text-xs ${info.cls}`}>{info.text}</p> : null;
                          })()}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell><StatusBadge status={park.protocol_status} /></TableCell>
                    <TableCell>{park.assigned_formen_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit ? (
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(park)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        ) : canView ? (
                          <Button variant="ghost" size="sm" onClick={() => handleView(park)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : null}
                        {canDelete && (
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => { setParkToDelete(park); setDeleteDialogOpen(true); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        /* Harita görünümü — protokol parklarına topluca bakış. Yalnızca
           `filteredParks` (backend kapsam + UI filtreleri) çizilir; böylece
           hiyerarşik kapsam haritada da korunur. */
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapIcon className="h-5 w-5 text-indigo-600" />
                <h3 className="font-semibold">Protokollü Park Haritası</h3>
              </div>
              <Badge variant="outline">
                {mapParks.length} / {filteredParks.length} koordinatlı park
              </Badge>
            </div>
            {mapParks.length > 0 ? (
              <div className="h-[600px] rounded-lg overflow-hidden border relative">
                <MapLayerToggle value={mapLayer} onChange={setMapLayer} floating />

                {/* Katman paneli — protokol parkının çevresini görünür kılar */}
                <NearbyLayerPanel
                  base={{
                    ...NEARBY_SOURCES.protokol,
                    count: mapParks.length,
                    checked: showProtokolLayer,
                    onChange: setShowProtokolLayer,
                  }}
                  layers={[
                    {
                      ...NEARBY_SOURCES.park,
                      count: parkPoints.length,
                      checked: showParksLayer,
                      onChange: setShowParksLayer,
                    },
                    {
                      ...NEARBY_SOURCES.pasif,
                      count: pasifPoints.length,
                      checked: showPasifLayer,
                      onChange: setShowPasifLayer,
                    },
                  ]}
                  loading={loadingNearby}
                  showClear={Boolean(linkLine)}
                  onClearLink={clearLink}
                />

                <MapContainer center={[39.925533, 32.866287]} zoom={12} style={{ height: '100%', width: '100%' }}>
                  <MapLayerTile layer={mapLayer} />
                  {/* Filtre değişince görünür noktalara otomatik oturt */}
                  <FitBoundsOnChange bounds={mapFitBounds} boundsKey={mapFitKey} />
                  {/* Park / pasif alan katmanları — küçük daireler */}
                  <NearbyPointMarkers points={visibleReferences} regionNameOf={getRegionName} />
                  {/* Protokol parkı ↔ seçilen referans bağlantı çizgisi */}
                  {linkLine && (
                    <Polyline positions={linkLine} pathOptions={{ color: '#4338ca', weight: 2.5, dashArray: '6 6' }}>
                      <Tooltip sticky direction="top">
                        {linkInfo?.refName} — {formatDistance(linkInfo?.distance)}
                      </Tooltip>
                    </Polyline>
                  )}
                  {/* Sınır poligonları (çizilmiş parklar için) */}
                  {showProtokolLayer && filteredParks
                    .filter(p => p.boundaries && p.boundaries.length >= 3)
                    .map(park => (
                      <Polygon key={`poly-${park.id}`} positions={park.boundaries} pathOptions={{ color: '#4f46e5', fillColor: '#818cf8', fillOpacity: 0.25 }} />
                    ))}
                  {/* Konum işaretçileri + bilgi balonu */}
                  {showProtokolLayer && mapParks.map(park => (
                      <Marker
                        key={park.id}
                        position={[park._lat, park._lng]}
                        icon={pinIcon(NEARBY_SOURCES.protokol.color)}
                      >
                        <Tooltip direction="top" offset={[0, -12]}>
                          <span className="font-medium">{park.name}</span>
                        </Tooltip>
                        <Popup>
                          <div className="min-w-[250px] max-w-[320px] space-y-2">
                            <h3 className="font-bold text-base text-indigo-700 border-b pb-1">{park.name}</h3>
                            {park.neighborhood && (
                              <p className="text-sm"><span className="font-medium text-gray-600">Mahalle:</span> {park.neighborhood}</p>
                            )}
                            {park.address && (
                              <p className="text-sm">
                                <span className="font-medium text-gray-600">Adres:</span>{' '}
                                <a
                                  href={buildDirectionsUrl(park._lat, park._lng, park.address)}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-indigo-700 underline-offset-2 hover:underline"
                                  title="Yol tarifi al ve git"
                                >
                                  {park.address}
                                </a>
                              </p>
                            )}
                            <p className="text-sm"><span className="font-medium text-gray-600">Bölge:</span> {getRegionName(park.region_id)}</p>
                            {park.counterparty_name && (
                              <p className="text-sm"><span className="font-medium text-gray-600">Site Yönetimi:</span> {park.counterparty_name}</p>
                            )}
                            {park.protocol_end_date && (
                              <p className="text-sm"><span className="font-medium text-gray-600">Bitiş:</span> {park.protocol_end_date}</p>
                            )}
                            {park.assigned_formen_name && (
                              <p className="text-sm"><span className="font-medium text-gray-600">Formen:</span> {park.assigned_formen_name}</p>
                            )}
                            <div className="pt-1"><StatusBadge status={park.protocol_status} /></div>
                            {/* En yakın park / pasif alan — "burası neyin yanında?" */}
                            <NearestPlaces
                              nearest={nearestByParkId[park.id] || []}
                              loading={loadingNearby}
                              title="En yakın"
                              onSelect={(ref) => focusReference(park, ref)}
                            />
                            <div className="flex gap-1 border-t pt-2">
                              {canView && (
                                <Button size="sm" variant="outline" className="flex-1" onClick={() => handleView(park)}>
                                  <Eye className="h-3 w-3 mr-1" /> Detaylar
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                                onClick={() => openInNewTab(buildDirectionsUrl(park._lat, park._lng, park.address))}
                              >
                                <Navigation className="h-3 w-3 mr-1" /> Yol Tarifi
                              </Button>
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                </MapContainer>
              </div>
            ) : (
              <div className="h-[400px] flex flex-col items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed">
                <MapIcon className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg font-medium">Koordinatı olan protokol park bulunamadı</p>
                <p className="text-gray-400 text-sm mt-1">Filtreleri temizleyip tekrar deneyebilirsiniz</p>
              </div>
            )}

            {/* Koordinatı olmayan parklar haritada çizilemez — hangileri olduğunu
                göster ki listedeki sayı ile harita arasındaki fark anlaşılsın. */}
            {filteredParks.length > mapParks.length && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="mb-2 text-sm font-medium text-amber-800">
                  ⚠️ Koordinatı girilmemiş {filteredParks.length - mapParks.length} park haritada gösterilemiyor
                </p>
                <div className="flex flex-wrap gap-2">
                  {filteredParks
                    .filter(p => toCoord(p.latitude) === null || toCoord(p.longitude) === null)
                    .map(p => (
                      <Badge key={p.id} variant="outline" className="bg-white text-amber-800">
                        {p.name}
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Create / Edit / View Modal (sürüklenebilir — başlıktan tutup her yöne taşınır) */}
      <DraggableModal
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title={isFormReadOnly ? '📋 Protokollü Park Detayları' : (isEditing ? 'Protokollü Park Düzenle' : 'Yeni Protokollü Park')}
        subtitle={isFormReadOnly ? 'Bu kaydı görüntülüyorsunuz' : 'Protokollü park bilgilerini girin'}
        maxWidth="max-w-4xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>{isFormReadOnly ? 'Kapat' : 'İptal'}</Button>
            {!isFormReadOnly && (
              <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">{isEditing ? 'Güncelle' : 'Kaydet'}</Button>
            )}
          </div>
        }
      >
          {isFormReadOnly && (
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                ⚠️ Bu formu sadece görüntüleme modunda açtınız. Değişiklik yapamazsınız.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <Label>Protokollü Park Adı *</Label>
                <Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Örn: Çiğdem Sitesi Parkı" disabled={isFormReadOnly} />
              </div>

              <div>
                <Label>Mahalle</Label>
                <Input value={form.neighborhood} onChange={(e) => setForm(prev => ({ ...prev, neighborhood: e.target.value }))} placeholder="Mahalle adı" disabled={isFormReadOnly} />
              </div>

              <div>
                <Label>Açık Adres</Label>
                <Textarea value={form.address} onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))} placeholder="Detaylı adres bilgisi" rows={2} disabled={isFormReadOnly} />
              </div>

              <div>
                <Label>Protokol No</Label>
                <Input value={form.protocol_no} onChange={(e) => setForm(prev => ({ ...prev, protocol_no: e.target.value }))} placeholder="Örn: 2024/137" disabled={isFormReadOnly} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Protokol Başlangıç</Label>
                  <DatePicker value={form.protocol_date} onChange={(v) => setForm(prev => ({ ...prev, protocol_date: v }))} disabled={isFormReadOnly} />
                </div>
                <div>
                  <Label>Protokol Bitiş</Label>
                  <DatePicker value={form.protocol_end_date} onChange={(v) => setForm(prev => ({ ...prev, protocol_end_date: v }))} min={form.protocol_date || undefined} disabled={isFormReadOnly} />
                  {(() => {
                    const info = expiryInfo(form.protocol_end_date);
                    return info ? <p className={`text-xs mt-1 flex items-center gap-1 ${info.cls}`}><Clock className="h-3 w-3" />{info.text}</p> : null;
                  })()}
                </div>
              </div>

              <div>
                <Label>Site Yönetimi (Karşı Taraf)</Label>
                <Input value={form.counterparty_name} onChange={(e) => setForm(prev => ({ ...prev, counterparty_name: e.target.value }))} placeholder="Örn: Çiğdem Sitesi Yönetimi" disabled={isFormReadOnly} />
              </div>

              <div>
                <Label>Yetkili / İletişim</Label>
                <Input value={form.counterparty_contact} onChange={(e) => setForm(prev => ({ ...prev, counterparty_contact: e.target.value }))} placeholder="Ad Soyad / Telefon" disabled={isFormReadOnly} />
              </div>

              <div>
                <Label>Bölge</Label>
                <SearchableSelect
                  value={form.region_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, region_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allLabel="Bölge Yok"
                  allValue="none"
                  searchPlaceholder="Bölge ara..."
                  options={regions.map(region => ({ id: region.id, name: region.name }))}
                />
              </div>

              <div>
                <Label>Formen</Label>
                <SearchableSelect
                  value={form.assigned_formen_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_formen_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allLabel="Atanmamış"
                  allValue="none"
                  searchPlaceholder="Formen ara..."
                  options={formens.map(p => ({ id: p.id, name: p.name }))}
                />
              </div>

              <div>
                <Label>Bölge Mühendisi</Label>
                <SearchableSelect
                  value={form.assigned_bolge_muhendisi_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_bolge_muhendisi_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allLabel="Atanmamış"
                  allValue="none"
                  searchPlaceholder="Bölge Mühendisi ara..."
                  options={bolgeMuhendisleri.map(p => ({ id: p.id, name: p.name }))}
                />
              </div>

              <div>
                <Label>Müdür Yardımcısı</Label>
                <SearchableSelect
                  value={form.assigned_mudur_yardimcisi_id || "none"}
                  onChange={(v) => setForm(prev => ({ ...prev, assigned_mudur_yardimcisi_id: v === "none" ? "" : v }))}
                  disabled={isFormReadOnly}
                  allLabel="Atanmamış"
                  allValue="none"
                  searchPlaceholder="Müdür Yardımcısı ara..."
                  options={mudurYardimcilari.map(p => ({ id: p.id, name: p.name }))}
                />
              </div>

              <div>
                <Label>Notlar</Label>
                <Textarea value={form.notes} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Ek notlar..." rows={3} disabled={isFormReadOnly} />
              </div>
            </div>

            {/* Right Column - Map + Documents */}
            <div className="space-y-4">
              <div>
                <Label>Harita</Label>
                {!isFormReadOnly && (
                  <div className="flex gap-2 mb-2">
                    <Button type="button" variant={drawingMode ? "default" : "outline"} size="sm" onClick={() => setDrawingMode(!drawingMode)}>
                      {drawingMode ? "Çizimi Durdur" : "Sınır Çiz"}
                    </Button>
                    {drawnPoints.length > 0 && (
                      <>
                        <Button type="button" variant="outline" size="sm" onClick={handleSaveBoundaries}>Sınırı Kaydet</Button>
                        <Button type="button" variant="outline" size="sm" onClick={handleClearBoundaries}>Sınırı Temizle</Button>
                      </>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mb-2">
                  {isFormReadOnly ? "Salt okunur modda görüntüleniyor" : (drawingMode ? "Haritaya tıklayarak sınır noktalarını ekleyin" : "Konum seçmek için haritaya tıklayın")}
                </p>
              </div>

              <div className="h-[320px] rounded-lg overflow-hidden border relative">
                <MapLayerToggle value={formMapLayer} onChange={setFormMapLayer} floating />
                {!isFormReadOnly && (
                  <MapSearchBox apiBase={apiUrl} onResult={(lat, lng) => setFormSearchCenter([lat, lng])} />
                )}
                <MapContainer center={mapCenter} zoom={15} style={{ height: '100%', width: '100%' }}>
                  <MapLayerTile layer={formMapLayer} />
                  <RecenterMap center={formSearchCenter} />
                  {/* Pin, protokol park katman rengiyle aynı — harita görünümüyle tutarlı */}
                  {form.latitude && form.longitude && (
                    <Marker position={[form.latitude, form.longitude]} icon={pinIcon(NEARBY_SOURCES.protokol.color)} />
                  )}
                  {!isFormReadOnly && (drawingMode ? (
                    <BoundaryMarker onPointAdd={handleBoundaryPointAdd} />
                  ) : (
                    <LocationMarker onLocationSelect={handleLocationSelect} />
                  ))}
                  {drawnPoints.length >= 3 && (
                    <Polygon positions={drawnPoints} pathOptions={{ color: '#4f46e5', fillColor: '#818cf8', fillOpacity: 0.3 }} />
                  )}
                  {/* Çizilen sınır köşe noktaları — mavi ikon yerine küçük indigo daireler
                      (kafa karışmasın; mavi ikon sadece merkez konum için). */}
                  {drawnPoints.map((point, idx) => (
                    <CircleMarker
                      key={idx}
                      center={point}
                      radius={5}
                      pathOptions={{ color: '#3730a3', fillColor: '#6366f1', fillOpacity: 1, weight: 2 }}
                    >
                      <Tooltip direction="top" offset={[0, -6]}>{idx + 1}. nokta</Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>

              {drawnPoints.length > 0 && (
                <p className="text-sm text-gray-600">
                  {drawnPoints.length} nokta çizildi
                  {form.area && ` • Tahmini alan: ${form.area.toLocaleString()} m²`}
                </p>
              )}

              {/* Documents */}
              <div>
                <Label>Protokol Evrakları</Label>
                {!isFormReadOnly && (
                  <div className="mt-2">
                    <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" className="hidden" onChange={handleUploadDocument} />
                    <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "Yükleniyor..." : "Evrak Yükle"}
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">PDF, görsel, Word veya Excel</p>
                  </div>
                )}
                <div className="mt-2 space-y-2">
                  {(form.protocol_documents || []).length === 0 ? (
                    <p className="text-sm text-gray-400">Henüz evrak yok</p>
                  ) : (
                    (form.protocol_documents || []).map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 rounded border px-3 py-2 text-sm">
                        <a href={docUrl(url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-indigo-600 hover:underline truncate">
                          <FileText className="h-4 w-4 shrink-0" />
                          <span className="truncate">Evrak {idx + 1}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                        {!isFormReadOnly && (
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => handleRemoveDocument(url)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Çim Alanı */}
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-1">
                  <Sprout className="h-4 w-4 text-green-600" />
                  Çim Alanı (m²)
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={form.grass_area ?? ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    grass_area: e.target.value === '' ? null : Math.max(0, parseFloat(e.target.value) || 0),
                  }))}
                  placeholder="0"
                  disabled={isFormReadOnly}
                />
              </div>

              {/* Envanter adetleri */}
              <div>
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-blue-600" />
                  <Label className="mb-0">Envanter</Label>
                </div>
                <p className="text-xs text-gray-500 mt-1 mb-2">
                  Adetleri girin — sekme istatistikleri ve Envanter Özeti bu değerlerden hesaplanır.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {INVENTORY_CATEGORIES.map(cat => {
                    const Icon = cat.Icon;
                    return (
                      <div key={cat.key}>
                        <Label className="text-xs flex items-center gap-1.5 mb-1">
                          <Icon className={`h-3.5 w-3.5 ${cat.icon}`} />
                          {cat.label}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={form[cat.key] ?? ''}
                          onChange={(e) => handleCountChange(cat.key, e.target.value)}
                          placeholder="0"
                          disabled={isFormReadOnly}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tesisler */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Dumbbell className="h-4 w-4 text-orange-600" />
                  <Label className="mb-0">Tesisler & Spor Sahaları</Label>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[...FACILITY_ITEMS, ...SPORTS_ITEMS].map(item => (
                    <label
                      key={item.key}
                      className={`flex items-center gap-2 rounded border px-3 py-2 text-sm ${isFormReadOnly ? 'opacity-70' : 'cursor-pointer hover:bg-gray-50'}`}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-indigo-600"
                        checked={!!form[item.key]}
                        onChange={() => handleToggle(item.key)}
                        disabled={isFormReadOnly}
                      />
                      {item.emoji
                        ? <span className="text-base leading-none">{item.emoji}</span>
                        : <item.Icon className={`h-4 w-4 ${item.color}`} />}
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sulama Sistemi */}
              <div>
                <Label className="text-sm flex items-center gap-1.5 mb-1">
                  <Droplets className="h-4 w-4 text-blue-500" />
                  Sulama Sistemi
                </Label>
                <SearchableSelect
                  value={form.irrigation_system || 'yok'}
                  onChange={(value) => setForm(prev => ({ ...prev, irrigation_system: value }))}
                  disabled={isFormReadOnly}
                  showAllOption={false}
                  placeholder="Sulama sistemi seçin"
                  searchPlaceholder="Sulama sistemi ara..."
                  options={[
                    { id: "yok", name: "Yok" },
                    { id: "otomatik", name: "Otomatik" },
                    { id: "manuel", name: "Manuel" },
                    { id: "hortumla", name: "Hortumla" },
                  ]}
                />
              </div>
            </div>
          </div>
      </DraggableModal>

      {/* Delete Confirmation — ucuncu onay: park adi elle yazilmadan Sil acilmaz.
          Ana Parklar sekmesindeki silme onayiyla ayni kural. */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Protokollü Parkı Sil</DialogTitle>
            <DialogDescription>
              &quot;{parkToDelete?.name}&quot; protokol parkını silmek üzeresiniz. Bu işlem geri alınamaz;
              parkın envanteri, protokol bilgileri ve evrakları da listelerden düşer.
            </DialogDescription>
          </DialogHeader>

          <DeleteNameConfirm
            name={parkToDelete?.name}
            value={deleteConfirmText}
            onChange={setDeleteConfirmText}
            label="parkın adını"
          />

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={!deleteConfirmed}>
              Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İstatistik kırılım modalı — bir özet öğesine tıklanınca o kritere uyan
          parkları (nerede oldukları: mahalle, bölge, formen) listeler. */}
      <Dialog open={!!detail} onOpenChange={(open) => { if (!open) { setDetail(null); setDetailQuery(''); } }}>
        <DialogContent className="max-w-lg">
          {(() => {
            const allRows = detail?.rows || [];
            const term = normalizeSearchText(detailQuery);
            const rows = term
              ? allRows.filter(({ park }) => normalizeSearchText(park.name).includes(term))
              : allRows;
            return (
              <>
          <DialogHeader>
            <DialogTitle>{detail?.title}</DialogTitle>
            <DialogDescription>
              {term
                ? `${rows.length} / ${allRows.length} protokol park`
                : `${allRows.length} protokol park`}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Park adı ara..."
              aria-label="Park adı ara"
              value={detailQuery}
              onChange={(e) => setDetailQuery(e.target.value)}
              className="pl-10 pr-9 h-9"
            />
            {detailQuery !== '' && (
              <button
                type="button"
                onClick={() => setDetailQuery('')}
                aria-label="Aramayı temizle"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 -mx-1 px-1">
            {rows.length === 0 ? (
              <p className="text-center text-gray-500 py-8 text-sm">
                Aramaya uyan protokol park bulunamadı.
              </p>
            ) : rows.map(({ park, extra }) => {
              const clickable = canView;
              const RowTag = clickable ? 'button' : 'div';
              return (
                <RowTag
                  key={park.id}
                  type={clickable ? 'button' : undefined}
                  onClick={clickable ? () => { setDetail(null); handleView(park); } : undefined}
                  className={`w-full text-left rounded border px-3 py-2 transition-colors ${clickable ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{park.name}</span>
                    {extra ? <Badge variant="secondary">{extra}</Badge> : <StatusBadge status={park.protocol_status} />}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                    {park.neighborhood && (
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{park.neighborhood}</span>
                    )}
                    <span>Bölge: {getRegionName(park.region_id)}</span>
                    {park.assigned_formen_name && (
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{park.assigned_formen_name}</span>
                    )}
                    {(() => {
                      const info = expiryInfo(park.protocol_end_date);
                      return info ? (
                        <span className={`flex items-center gap-1 ${info.cls}`}><Clock className="h-3 w-3" />{info.text}</span>
                      ) : null;
                    })()}
                  </div>
                </RowTag>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDetail(null); setDetailQuery(''); }}>Kapat</Button>
          </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </TabsContent>
  );
}
